import { Link, getRouteApi, useNavigate, useRouter } from '@tanstack/react-router';
import { useMemo, useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';
import {
  CHRONOLOGY_DESCRIPTION_MAX,
  CHRONOLOGY_NAME_MAX,
  SortKeyError,
  describeRange,
  inCalendar,
  markerName,
  parseSortKey,
  type Audience,
  type CalendarSystem,
  type ChronologyDate,
  type Era,
  type EntrySummary,
  type MarkerSummary,
  type Timeline,
  type TimelineEvent,
  type TimelineNode,
} from '@realmery/realm-core';
import { RealmClientError, realms } from '../db/client.ts';
import { useReadOnly } from './readOnly.ts';
import { AudienceField, audienceName } from './AudienceField.tsx';
import { realmFile, type ChronologyView } from '../db/protocol.ts';
import { settings } from '../settings.ts';
import { DateFields, Picker, draftOf, type DateDraft } from './ChronologyFields.tsx';
import { ActionMenu } from '../components/ActionMenu.tsx';
import { scaleGaps, type Gap } from './chronologyGaps.ts';

/**
 * A realm's Chronology (docs/PARITY-STEP-3-PLAN.md section 4), as the desktop's: timelines
 * in a tree, an open timeline's eras as cards of events by date, then Unfiled Events, or
 * every event of it and its sub-timelines merged. Dates are written the realm's own way, with
 * a sort key that orders them; with an active calendar, Pick a date fills both in (decision 1).
 */

const route = getRouteApi('/_app/realm/$realmKey/chronology');
const realmRoute = getRouteApi('/_app/realm/$realmKey');

const message = (e: unknown, fallback: string) => (e instanceof RealmClientError ? e.message : fallback);
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

type Panel =
  | { kind: 'none' }
  | { kind: 'new-timeline'; parentId: string | null }
  | { kind: 'edit-timeline' }
  | { kind: 'delete-timeline'; impact: string }
  | { kind: 'new-era' }
  | { kind: 'edit-era'; era: Era }
  | { kind: 'delete-era'; era: Era }
  | { kind: 'new-event' }
  | { kind: 'edit-event'; event: TimelineEvent }
  | { kind: 'delete-event'; event: TimelineEvent; sceneLinks: number };

export function ChronologyPage() {
  const view = route.useLoaderData();
  const search = route.useSearch();
  const { realmKey } = route.useParams();
  const { entries } = realmRoute.useLoaderData();
  const readOnly = useReadOnly();
  const file = realmFile(realmKey);
  const router = useRouter();
  const navigate = useNavigate();
  const [panel, setPanel] = useState<Panel>({ kind: 'none' });
  const [error, setError] = useState('');
  const timeline = view.timeline;

  async function act(work: () => Promise<unknown>, fallback: string) {
    setError('');
    try {
      await work();
      setPanel({ kind: 'none' });
      await router.invalidate();
    } catch (e) {
      setError(message(e, fallback));
    }
  }

  const open = (timelineId: string | null, merged = false) =>
    navigate({ to: '/realm/$realmKey/chronology', params: { realmKey }, search: timelineId ? { timeline: timelineId, ...(merged ? { merged: true } : {}) } : {} });

  // Back to the Chronology after deleting the open timeline, unless you've already gone elsewhere.
  const leaveDeleted = () => (router.state.location.pathname.endsWith('/chronology') ? open(null) : undefined);

  async function askDeleteTimeline() {
    if (!timeline) return;
    if (!settings()['general.confirmDeletes']) return void act(() => realms.deleteTimeline(file, timeline.id).then(leaveDeleted), 'The timeline could not be deleted.');
    try {
      const impact = await realms.timelineDeletionImpact(file, timeline.id);
      const parts = [
        impact.subTimelines > 0 && plural(impact.subTimelines, 'sub-timeline', 'sub-timelines'),
        impact.eras > 0 && plural(impact.eras, 'era', 'eras'),
        impact.events > 0 && plural(impact.events, 'event', 'events'),
      ].filter(Boolean);
      let text = parts.length > 0 ? `This deletes its ${parts.join(', ')} too.` : 'It has no eras or events.';
      if (impact.sceneLinks > 0) text += ` ${plural(impact.sceneLinks, 'Story Studio scene loses its link', 'Story Studio scenes lose their links')} to them.`;
      setPanel({ kind: 'delete-timeline', impact: text });
    } catch (e) {
      setError(message(e, 'The timeline could not be checked.'));
    }
  }

  async function askDeleteEvent(event: TimelineEvent) {
    if (!settings()['general.confirmDeletes']) return void act(() => realms.deleteEvent(file, event.id), 'The event could not be deleted.');
    try {
      setPanel({ kind: 'delete-event', event, sceneLinks: await realms.eventSceneLinks(file, event.id) });
    } catch (e) {
      setError(message(e, 'The event could not be checked.'));
    }
  }

  const eventActions = (event: TimelineEvent) => ({
    onEdit: () => setPanel({ kind: 'edit-event', event }),
    onDelete: () => void askDeleteEvent(event),
  });

  // Even spacing, or to scale: remembered in this browser.
  const [spacing, setSpacingState] = useState<Spacing>(() => readSpacing());
  const setSpacing = (next: Spacing) => {
    setSpacingState(next);
    try {
      localStorage.setItem(SPACING_KEY, next);
    } catch {
      // Private browsing: it's only for this visit.
    }
  };
  // Sub-timelines merged in: each can be shown or hidden here.
  const [hiddenSubs, setHiddenSubs] = useState<ReadonlySet<string>>(new Set());
  const subTimelines = useMemo(() => {
    const seen = new Map<string, Timeline>();
    for (const { timeline: t } of view.merged) if (t.id !== timeline?.id && !seen.has(t.id)) seen.set(t.id, t);
    return [...seen.values()];
  }, [view.merged, timeline?.id]);
  const colourOf = (timelineId: string) => SUB_TIMELINE_COLOURS[subTimelines.findIndex((t) => t.id === timelineId) % SUB_TIMELINE_COLOURS.length]!;
  const unfiled = view.events.filter((e) => e.eraId === null || !view.eras.some((era) => era.id === e.eraId));
  const context: EventListContext = { view, entries, realmKey, panel, file, act, setPanel, eventActions, spacing };

  return (
    <article className="document chronology-page">
      <p className="eyebrow">Chronology</p>
      <h1>Chronology</h1>
      <p className="lead">Your world's history, in order: timelines of eras and events, nested so parallel histories stay apart but can be read together.</p>
      {!readOnly && (
        <div className="actions">
          <button type="button" className="primary" onClick={() => setPanel({ kind: 'new-timeline', parentId: null })}>
            New timeline
          </button>
        </div>
      )}
      {error && (
        <p className="notice-error" role="alert">
          {error}
        </p>
      )}
      {view.calendarProblem && <p className="callout">The active calendar can't be used here: {view.calendarProblem}</p>}
      {panel.kind === 'new-timeline' && (
        <TimelineForm
          heading={panel.parentId ? `New sub-timeline of ${timeline?.name ?? ''}` : 'New timeline'}
          submit="Make timeline"
          onCancel={() => setPanel({ kind: 'none' })}
          onSave={(name, description) =>
            act(async () => {
              const { id } = await realms.createTimeline(file, name, description, panel.parentId);
              await open(id);
            }, 'The timeline could not be made.')
          }
        />
      )}

      <div className="chronology-layout">
        <nav className="timeline-tree" aria-label="Timelines">
          <h2 className="timeline-tree-heading">Timelines</h2>
          {view.tree.length === 0 ? <p className="meta">No timelines yet.</p> : <TimelineTree nodes={view.tree} selectedId={timeline?.id ?? null} realmKey={realmKey} />}
        </nav>

        <div className="timeline-view">
          {search.timeline && !timeline && <p className="meta">That timeline no longer exists. It may have been deleted.</p>}
          {!timeline && !search.timeline && view.tree.length > 0 && <p className="meta">Choose a timeline to open it.</p>}
          {timeline && (
            <section aria-labelledby="timeline-heading">
              <div className="timeline-head">
                <h2 id="timeline-heading">{timeline.name}</h2>
                {!readOnly && <div className="actions">
                  <button type="button" onClick={() => setPanel({ kind: 'new-event' })}>
                    New event
                  </button>
                  <button type="button" onClick={() => setPanel({ kind: 'new-era' })}>
                    New era
                  </button>
                  <ActionMenu
                    label={`More for ${timeline.name}`}
                    actions={[
                      { label: 'Edit timeline', onSelect: () => setPanel({ kind: 'edit-timeline' }) },
                      { label: 'New sub-timeline', onSelect: () => setPanel({ kind: 'new-timeline', parentId: timeline.id }) },
                      { label: 'Delete timeline…', onSelect: () => void askDeleteTimeline(), danger: true },
                    ]}
                  />
                </div>}
              </div>
              {timeline.description && <p className="pre-line timeline-description">{timeline.description}</p>}
              <div className="timeline-show">
                <label className="check">
                  <input type="checkbox" checked={search.merged === true} onChange={(e) => void open(timeline.id, e.currentTarget.checked)} />
                  Include sub-timelines
                </label>
                <span className="view-switch spacing-switch" role="group" aria-label="Spacing">
                  {(
                    [
                      ['even', 'Even'],
                      ['scale', 'To scale'],
                    ] as const
                  ).map(([id, name]) => (
                    <button
                      key={id}
                      type="button"
                      className={spacing === id ? 'tab selected' : 'tab'}
                      aria-pressed={spacing === id}
                      title={id === 'even' ? 'The same space between every event' : 'Space between events as long as the time between them, with long gaps marked'}
                      onClick={() => setSpacing(id)}
                    >
                      {name}
                    </button>
                  ))}
                </span>
                {search.merged &&
                  subTimelines.map((t) => (
                    <label key={t.id} className="check timeline-colour" style={{ '--timeline-colour': colourOf(t.id) } as CSSProperties}>
                      <input
                        type="checkbox"
                        checked={!hiddenSubs.has(t.id)}
                        onChange={(e) => {
                          const next = new Set(hiddenSubs);
                          if (e.currentTarget.checked) next.delete(t.id);
                          else next.add(t.id);
                          setHiddenSubs(next);
                        }}
                      />
                      {t.name}
                    </label>
                  ))}
              </div>

              {panel.kind === 'edit-timeline' && (
                <TimelineForm
                  heading="Edit timeline"
                  submit="Save"
                  initial={timeline}
                  onCancel={() => setPanel({ kind: 'none' })}
                  onSave={(name, description) => act(() => realms.updateTimeline(file, timeline.id, name, description), 'The timeline could not be saved.')}
                />
              )}
              {panel.kind === 'delete-timeline' && (
                <Confirm
                  label={`Delete ${timeline.name}`}
                  text={`Delete “${timeline.name}”? ${panel.impact} This can't be undone.`}
                  button="Delete timeline"
                  onCancel={() => setPanel({ kind: 'none' })}
                  onConfirm={() => act(() => realms.deleteTimeline(file, timeline.id).then(leaveDeleted), 'The timeline could not be deleted.')}
                />
              )}
              {panel.kind === 'new-era' && (
                <DatedForm
                  kind="era"
                  calendar={view.calendar}
                  onCancel={() => setPanel({ kind: 'none' })}
                  onSave={(input) => act(() => realms.createEra(file, timeline.id, input), 'The era could not be made.')}
                />
              )}
              {panel.kind === 'new-event' && (
                <DatedForm
                  kind="event"
                  calendar={view.calendar}
                  eras={view.eras}
                  entries={entries}
                  markers={view.markers}
                  onCancel={() => setPanel({ kind: 'none' })}
                  onSave={(input) => act(() => realms.createEvent(file, timeline.id, input), 'The event could not be made.')}
                />
              )}

              {search.merged ? (
                view.merged.length === 0 ? (
                  <p className="meta">No events here or in its sub-timelines yet.</p>
                ) : (
                  <MergedSpine
                    label={`Every event, ${timeline.name}'s and its sub-timelines'`}
                    items={view.merged.filter(({ timeline: t }) => t.id === timeline.id || !hiddenSubs.has(t.id))}
                    ownId={timeline.id}
                    colourOf={colourOf}
                    context={context}
                  />
                )
              ) : (
                <div className="spine-eras">
                  {view.eras.map((era) => (
                    <section key={era.id} className="era-band" aria-labelledby={`era-${era.id}`}>
                      <header className="era-head">
                        <h3 id={`era-${era.id}`}>{era.name}</h3>
                        <DateLine date={era.start} end={era.end} calendar={view.calendar} />
                        {!readOnly && <ActionMenu
                          label={`Actions for ${era.name}`}
                          actions={[
                            { label: 'Edit', onSelect: () => setPanel({ kind: 'edit-era', era }) },
                            {
                              label: 'Delete…',
                              danger: true,
                              onSelect: () => (settings()['general.confirmDeletes'] ? setPanel({ kind: 'delete-era', era }) : void act(() => realms.deleteEra(file, era.id), 'The era could not be deleted.')),
                            },
                          ]}
                        />}
                      </header>
                      {era.description && <p className="pre-line era-description">{era.description}</p>}
                      {panel.kind === 'edit-era' && panel.era.id === era.id && (
                        <DatedForm
                          kind="era"
                          calendar={view.calendar}
                          initial={era}
                          onCancel={() => setPanel({ kind: 'none' })}
                          onSave={(input) => act(() => realms.updateEra(file, era.id, input), 'The era could not be saved.')}
                        />
                      )}
                      {panel.kind === 'delete-era' && panel.era.id === era.id && (
                        <Confirm
                          label={`Delete ${era.name}`}
                          text={`Delete “${era.name}”? Its events are kept, unfiled.`}
                          button="Delete era"
                          onCancel={() => setPanel({ kind: 'none' })}
                          onConfirm={() => act(() => realms.deleteEra(file, era.id), 'The era could not be deleted.')}
                        />
                      )}
                      <EventList events={view.events.filter((e) => e.eraId === era.id)} none="No events in this era yet." {...context} />
                    </section>
                  ))}
                  {(unfiled.length > 0 || view.eras.length === 0) && (
                    <section className="era-band unfiled-events" aria-labelledby="unfiled-heading">
                      <header className="era-head">
                        <h3 id="unfiled-heading">{view.eras.length === 0 ? 'Events' : 'Unfiled Events'}</h3>
                      </header>
                      <EventList events={unfiled} none="No events yet." {...context} />
                    </section>
                  )}
                </div>
              )}
            </section>
          )}
        </div>
      </div>
    </article>
  );
}

/** Each sub-timeline merged in gets its own colour, as on a key. */
const SUB_TIMELINE_COLOURS = ['#2E86C1', '#8E44AD', '#16A085', '#C0392B', '#D35400', '#7F8C8D'];

// ---- the tree ----

function TimelineTree({ nodes, selectedId, realmKey }: { nodes: readonly TimelineNode[]; selectedId: string | null; realmKey: string }) {
  return (
    <ul>
      {nodes.map(({ timeline, children }) => (
        <li key={timeline.id}>
          <Link
            to="/realm/$realmKey/chronology"
            params={{ realmKey }}
            search={{ timeline: timeline.id }}
            aria-current={timeline.id === selectedId ? 'page' : undefined}
            className={timeline.id === selectedId ? 'selected' : undefined}
          >
            {timeline.name}
          </Link>
          {children.length > 0 && <TimelineTree nodes={children} selectedId={selectedId} realmKey={realmKey} />}
        </li>
      ))}
    </ul>
  );
}

// ---- events ----

interface EventListContext {
  readonly view: ChronologyView;
  readonly entries: readonly EntrySummary[];
  readonly realmKey: string;
  readonly panel: Panel;
  readonly file: string;
  readonly act: (work: () => Promise<unknown>, fallback: string) => Promise<void>;
  readonly setPanel: (panel: Panel) => void;
  readonly eventActions: (event: TimelineEvent) => { onEdit: () => void; onDelete: () => void };
  readonly spacing: Spacing;
}

type Spacing = 'even' | 'scale';
const SPACING_KEY = 'realmery.chronology.spacing';
function readSpacing(): Spacing {
  try {
    return localStorage.getItem(SPACING_KEY) === 'scale' ? 'scale' : 'even';
  } catch {
    return 'even';
  }
}

function EventList({ events, none, ...context }: EventListContext & { events: readonly TimelineEvent[]; none: string }) {
  if (events.length === 0) return <p className="meta spine-none">{none}</p>;
  const gaps = context.spacing === 'scale' ? scaleGaps(events.map((e) => e.start.sortKey), context.view.calendar) : null;
  return (
    <ol className="spine">
      {events.map((event, i) => (
        <EventItem key={event.id} event={event} gap={gaps?.[i] ?? null} {...context} />
      ))}
    </ol>
  );
}

function MergedSpine({
  label,
  items,
  ownId,
  colourOf,
  context,
}: {
  label: string;
  items: ChronologyView['merged'];
  ownId: string;
  colourOf: (timelineId: string) => string;
  context: EventListContext;
}) {
  const gaps = context.spacing === 'scale' ? scaleGaps(items.map(({ event }) => event.start.sortKey), context.view.calendar) : null;
  return (
    <ol className="spine" aria-label={label}>
      {items.map(({ timeline: t, event }, i) => (
        <EventItem key={event.id} event={event} from={t.id === ownId ? null : t} colour={t.id === ownId ? null : colourOf(t.id)} gap={gaps?.[i] ?? null} {...context} />
      ))}
    </ol>
  );
}

function EventItem({
  event,
  from = null,
  colour = null,
  gap = null,
  view,
  entries,
  realmKey,
  panel,
  file,
  act,
  setPanel,
  eventActions,
}: EventListContext & { event: TimelineEvent; from?: Timeline | null; colour?: string | null; gap?: Gap | null }) {
  const byId = useMemo(() => new Map(entries.map((e) => [e.id, e])), [entries]);
  const pins = useMemo(() => new Map(view.markers.map((m) => [m.id, m])), [view.markers]);
  const { onEdit, onDelete } = eventActions(event);
  // An event from a sub-timeline is edited there, where its eras are.
  const readOnly = useReadOnly();
  const editable = from === null && !readOnly;
  const uncertain = event.start.uncertain || (event.end?.uncertain ?? false);
  return (
    <li
      className={[uncertain ? 'event-item uncertain' : 'event-item', colour ? 'from-sub-timeline' : ''].join(' ').trim()}
      style={{ ...(colour ? ({ '--timeline-colour': colour } as CSSProperties) : {}), ...(gap && gap.px > 0 ? { marginTop: `${gap.px}px` } : {}) }}
    >
      {gap?.marker && (
        <p className="spine-gap" aria-label={`A gap of ${gap.marker.replace(/⋯/g, '').trim()}`}>
          {gap.marker}
        </p>
      )}
      <DateLine date={event.start} end={event.end} calendar={view.calendar} />
      <div className="event-head">
        <strong>{event.title}</strong>
        {from && <span className="chip timeline-chip">{from.name}</span>}
        {event.audience.role !== 'READER' && (
          <span className="chip" title="Who may see this event">
            {audienceName(event.audience)}
          </span>
        )}
        {editable && (
          <ActionMenu
            label={`Actions for ${event.title}`}
            actions={[
              { label: 'Edit', onSelect: onEdit },
              { label: 'Delete…', onSelect: onDelete, danger: true },
            ]}
          />
        )}
      </div>
      {event.description && <p className="pre-line event-description">{event.description}</p>}
      {(event.entryIds.length > 0 || event.markerIds.length > 0) && (
        <ul className="chips event-links" aria-label="Linked entries and map pins">
          {event.entryIds.map((id) => {
            const entry = byId.get(id);
            return (
              <li key={id}>
                {entry ? (
                  <Link to="/realm/$realmKey/entry/$entryId" params={{ realmKey, entryId: id }} className="chip">
                    {entry.title} →
                  </Link>
                ) : (
                  <span className="chip broken">(missing entry)</span>
                )}
              </li>
            );
          })}
          {event.markerIds.map((id) => {
            const pin = pins.get(id);
            return (
              <li key={id}>
                {pin ? (
                  <Link className="chip pin" to="/realm/$realmKey/atlas/$mapId" params={{ realmKey, mapId: pin.mapId }} search={{ pin: pin.id }} title="Open it on its map in the Atlas">
                    {markerName(pin)}
                  </Link>
                ) : (
                  <span className="chip broken">(missing location)</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {panel.kind === 'edit-event' && panel.event.id === event.id && (
        <DatedForm
          kind="event"
          calendar={view.calendar}
          eras={view.eras}
          entries={entries}
          markers={view.markers}
          initial={event}
          onCancel={() => setPanel({ kind: 'none' })}
          onSave={(input) => act(() => realms.updateEvent(file, event.id, input), 'The event could not be saved.')}
        />
      )}
      {panel.kind === 'delete-event' && panel.event.id === event.id && (
        <Confirm
          label={`Delete ${event.title}`}
          text={`Delete “${event.title}”?${
            panel.sceneLinks > 0 ? ` ${plural(panel.sceneLinks, 'Story Studio scene links', 'Story Studio scenes link')} to it, and will lose the link.` : ''
          } This can't be undone.`}
          button="Delete event"
          onCancel={() => setPanel({ kind: 'none' })}
          onConfirm={() => act(() => realms.deleteEvent(file, event.id), 'The event could not be deleted.')}
        />
      )}
    </li>
  );
}

/**
 * A date once: as it was written ("Frostmoon 20, Year 17", with an end and "(uncertain)"),
 * then the active calendar's reading of it where that adds something. The sort key is the
 * engine's ordering number, so it's in the tooltip, not the text.
 */
function DateLine({ date, end, calendar }: { date: ChronologyDate; end: ChronologyDate | null; calendar: CalendarSystem | null }) {
  const written = describeRange(date, end);
  const inCal = calendar ? inCalendar(calendar, date) : null;
  // "Highsun 1, Year 1" and "Year 1, Highsun 1" are the same date in another order.
  const words = (text: string) => text.toLowerCase().split(/[^\p{L}\p{N}]+/u).filter(Boolean).sort().join(' ');
  const same = inCal !== null && words(date.label) === words(inCal);
  const shown = written.trim() !== '' ? written : (inCal ?? `Sort key ${date.sortKey.toString()}`);
  return (
    <p className="date-meta" title={`Sort key ${date.sortKey.toString()}${inCal ? ` · ${inCal} in the active calendar` : ''}`}>
      {shown}
      {inCal && !same && written.trim() !== '' && <span className="date-calendar"> · {inCal}</span>}
    </p>
  );
}

// ---- forms ----

function TimelineForm({
  heading,
  submit,
  initial,
  onSave,
  onCancel,
}: {
  heading: string;
  submit: string;
  initial?: Timeline;
  onSave: (name: string, description: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  return (
    <form
      className="panel"
      aria-label={heading}
      onSubmit={(e) => {
        e.preventDefault();
        void onSave(name, description);
      }}
    >
      <h3>{heading}</h3>
      <label>
        Name
        <input value={name} required maxLength={CHRONOLOGY_NAME_MAX} autoFocus placeholder="e.g. Global Timeline" onChange={(e) => setName(e.currentTarget.value)} />
      </label>
      <label>
        Description
        <textarea value={description} rows={3} maxLength={CHRONOLOGY_DESCRIPTION_MAX} placeholder="Optional" onChange={(e) => setDescription(e.currentTarget.value)} />
      </label>
      <FormButtons submit={submit} onCancel={onCancel} />
    </form>
  );
}

interface DatedInput {
  readonly name: string;
  readonly description: string;
  readonly start: ChronologyDate;
  readonly end: ChronologyDate | null;
  readonly eraId: string | null;
  readonly entryIds: readonly string[];
  readonly markerIds: readonly string[];
  /** An event's: who may see it. */
  readonly audience?: Audience;
}


/** An era's or an event's details, as the desktop's New Era and New Event dialogs. */
function DatedForm({
  kind,
  calendar,
  initial,
  eras = [],
  entries = [],
  markers = [],
  onSave,
  onCancel,
}: {
  kind: 'era' | 'event';
  calendar: CalendarSystem | null;
  initial?: Era | TimelineEvent;
  eras?: readonly Era[];
  entries?: readonly EntrySummary[];
  markers?: readonly MarkerSummary[];
  onSave: (input: DatedInput) => Promise<void>;
  onCancel: () => void;
}) {
  const event = initial && 'title' in initial ? initial : null;
  const [name, setName] = useState(event ? event.title : (initial as Era | undefined)?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [start, setStart] = useState(draftOf(initial?.start));
  const [hasEnd, setHasEnd] = useState(initial?.end != null);
  const [end, setEnd] = useState(draftOf(initial?.end));
  const [eraId, setEraId] = useState(event?.eraId ?? '');
  const [entryIds, setEntryIds] = useState<readonly string[]>(event?.entryIds ?? []);
  const [markerIds, setMarkerIds] = useState<readonly string[]>(event?.markerIds ?? []);
  const [audience, setAudience] = useState<Audience>(event?.audience ?? { role: 'READER', customLabel: '' });
  const [problem, setProblem] = useState('');
  const what = kind === 'era' ? 'era' : 'event';

  function submit(e: FormEvent) {
    e.preventDefault();
    try {
      const date = (d: DateDraft, which: string): ChronologyDate => {
        try {
          return { sortKey: parseSortKey(d.key), label: d.label, uncertain: d.uncertain };
        } catch (error) {
          throw new SortKeyError(`${which}: ${(error as Error).message}`);
        }
      };
      const input: DatedInput = {
        name,
        description,
        start: date(start, 'Start'),
        end: hasEnd ? date(end, 'End') : null,
        eraId: eraId === '' ? null : eraId,
        entryIds,
        markerIds,
        ...(kind === 'event' ? { audience } : {}),
      };
      setProblem('');
      void onSave(input);
    } catch (error) {
      setProblem((error as Error).message);
    }
  }

  return (
    <form className="panel dated-form" aria-label={initial ? `Edit ${what}` : `New ${what}`} onSubmit={submit}>
      <h3>{initial ? `Edit ${what}` : `New ${what}`}</h3>
      <label>
        {kind === 'era' ? 'Name' : 'Title'}
        <input
          value={name}
          required
          maxLength={CHRONOLOGY_NAME_MAX}
          autoFocus
          placeholder={kind === 'era' ? 'e.g. The First Age' : 'e.g. The Battle of Red Hollow'}
          onChange={(e) => setName(e.currentTarget.value)}
        />
      </label>
      <label>
        Description
        <textarea value={description} rows={3} maxLength={CHRONOLOGY_DESCRIPTION_MAX} placeholder="Optional" onChange={(e) => setDescription(e.currentTarget.value)} />
      </label>
      {kind === 'event' && (
        <label>
          Era
          <select value={eraId} onChange={(e) => setEraId(e.currentTarget.value)}>
            <option value="">(none)</option>
            {eras.map((era) => (
              <option key={era.id} value={era.id}>
                {era.name}
              </option>
            ))}
          </select>
        </label>
      )}
      <p className="meta">
        Write the date your own way (“Year 420”, “450 BCE”, “Third Age”). The sort key is a plain number that puts dates in order: lower comes first.
      </p>
      <DateFields legend="Start" value={start} onChange={setStart} calendar={calendar} />
      <label className="check">
        <input type="checkbox" checked={hasEnd} onChange={(e) => setHasEnd(e.currentTarget.checked)} />
        {kind === 'era' ? 'Has an end date' : 'Spans a date range'}
      </label>
      {hasEnd && <DateFields legend="End" value={end} onChange={setEnd} calendar={calendar} />}
      {kind === 'event' && (
        <>
          <Picker
            legend="Entries it involves"
            items={entries.map((e) => ({ id: e.id, name: e.title, note: e.typeName }))}
            chosen={entryIds}
            onChange={setEntryIds}
            searchLabel="Find an entry"
            none="The realm has no entries yet."
          />
          <Picker
            legend="Map pins"
            items={markers.map((m) => ({ id: m.id, name: markerName(m), note: '' }))}
            chosen={markerIds}
            onChange={setMarkerIds}
            searchLabel="Find a pin"
            none="The realm has no map pins yet. They're placed in the Atlas, on the desktop."
          />
          <AudienceField value={audience} onChange={(a) => setAudience(a)} />
          <p className="meta">Who may see it. A copy you share with players or readers leaves out events that aren&apos;t theirs.</p>
        </>
      )}
      {problem && (
        <p className="notice-error" role="alert">
          {problem}
        </p>
      )}
      <FormButtons submit={initial ? 'Save' : kind === 'era' ? 'Make era' : 'Make event'} onCancel={onCancel} />
    </form>
  );
}

function FormButtons({ submit, onCancel }: { submit: string; onCancel: () => void }) {
  return (
    <div className="actions">
      <button type="submit" className="primary">
        {submit}
      </button>
      <button type="button" onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
}

function Confirm({ label, text, button, onConfirm, onCancel }: { label: string; text: ReactNode; button: string; onConfirm: () => Promise<void>; onCancel: () => void }) {
  return (
    <div className="panel confirm" role="alertdialog" aria-label={label}>
      <p>{text}</p>
      <div className="actions">
        <button type="button" className="danger" onClick={() => void onConfirm()}>
          {button}
        </button>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
